import React, { useEffect, useRef, useState } from 'react';
import { chakra, shouldForwardProp, Box, VisuallyHidden } from '@chakra-ui/react';
import {
  motion,
  isValidMotionProp,
  useInView,
  useScroll,
  useTransform,
  animate,
} from 'framer-motion';
import useMotionAllowed from './useMotionAllowed';

/**
 * Motion primitives, tuned for a surgical center.
 *
 * The vocabulary is borrowed from the agency site: content arrives rather
 * than appears, panels settle into place, photography relaxes out of a slight
 * zoom. The amplitudes are deliberately smaller here. A marketing site can be
 * dramatic; a place people go for surgery should read calm and unhurried, so
 * every distance and angle is roughly a third of the original and every
 * duration is a little longer. Nothing flashes, spins, or glows.
 *
 * Every primitive degrades to "already in final position" when motion is not
 * allowed. That direction matters: a reveal that fails closed leaves the page
 * blank, which is the worst possible failure for a medical site.
 */

const MotionBox = chakra(motion.div, {
  shouldForwardProp: (prop) => isValidMotionProp(prop) || shouldForwardProp(prop),
});

/**
 * A long decelerating curve. Things travel most of the distance quickly and
 * then ease for a long time, which is what reads as "settling" rather than
 * "stopping".
 */
export const EASE = [0.16, 1, 0.3, 1];

export const fadeUp = {
  hidden: { opacity: 0, y: 26 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.85, ease: EASE } },
};

export const staggerParent = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.08, delayChildren: 0.05 } },
};

export const staggerChild = {
  hidden: { opacity: 0, y: 22 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: EASE } },
};

/** Final position, no movement. Used whenever motion is switched off. */
const settled = {
  hidden: { opacity: 1, y: 0 },
  visible: { opacity: 1, y: 0, transition: { duration: 0 } },
};

/**
 * Begin slightly before the element is fully on screen so it has finished
 * moving by the time the reader's eye arrives, and only ever play once.
 * Re-animating on every pass makes a long page feel restless.
 */
const VIEWPORT = { once: true, margin: '0px 0px -10% 0px' };

/** Reveals one block as it scrolls into view. */
export const Reveal = ({ children, ...props }) => {
  const allowed = useMotionAllowed();
  return (
    <MotionBox
      initial="hidden"
      whileInView="visible"
      viewport={VIEWPORT}
      variants={allowed ? fadeUp : settled}
      {...props}
    >
      {children}
    </MotionBox>
  );
};

/**
 * Cascades its children.
 *
 * Children must be RevealItem for the stagger to reach them: Framer Motion
 * propagates variants to children by name, so a plain div in between breaks
 * the chain silently.
 */
export const RevealGroup = ({ children, ...props }) => {
  const allowed = useMotionAllowed();
  return (
    <MotionBox
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: '0px 0px -8% 0px' }}
      variants={allowed ? staggerParent : settled}
      {...props}
    >
      {children}
    </MotionBox>
  );
};

export const RevealItem = ({ children, ...props }) => {
  const allowed = useMotionAllowed();
  return (
    <MotionBox variants={allowed ? staggerChild : settled} {...props}>
      {children}
    </MotionBox>
  );
};

/**
 * Photography that relaxes out of a slight zoom as it scrolls in.
 *
 * The image starts a little larger than its frame and settles to exactly fit,
 * which makes a static photograph feel like it is coming to rest. Kept subtle
 * (8%) so it reads as depth rather than as an effect.
 *
 * Wrap a single image. The frame clips the overscan, so the image must fill it.
 */
export const ZoomSettle = ({ children, from = 1.08, ...props }) => {
  const allowed = useMotionAllowed();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'center center'],
  });
  const scale = useTransform(scrollYProgress, [0, 1], [from, 1]);

  return (
    <Box ref={ref} overflow="hidden" {...props}>
      <MotionBox
        h="100%"
        w="100%"
        style={allowed ? { scale } : undefined}
        sx={{ '& img': { width: '100%', height: '100%', objectFit: 'cover' } }}
      >
        {children}
      </MotionBox>
    </Box>
  );
};

/**
 * Counts a figure up when it scrolls into view.
 *
 * Only use this on numbers that are confirmed and stable. A figure that
 * animates draws the eye and implies precision, so counting up to a number
 * nobody has verified is worse than showing it plainly.
 *
 * Renders the final value immediately when motion is off, and always ends on
 * the exact target rather than wherever the easing happened to land.
 */
export const CountUp = ({
  to,
  prefix = '',
  suffix = '',
  decimals = 0,
  duration = 1.4,
  ...props
}) => {
  const allowed = useMotionAllowed();
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '0px 0px -15% 0px' });
  const [value, setValue] = useState(allowed ? 0 : to);

  useEffect(() => {
    if (!allowed) {
      setValue(to);
      return undefined;
    }
    if (!inView) return undefined;

    const controls = animate(0, to, {
      duration,
      ease: EASE,
      onUpdate: (v) => setValue(v),
      onComplete: () => setValue(to),
    });
    return () => controls.stop();
  }, [inView, allowed, to, duration]);

  // The moving number is visual only. Assistive technology and crawlers get
  // the real figure, so nobody hears "zero" or a string of partial counts.
  const final = `${prefix}${Number(to).toFixed(decimals)}${suffix}`;

  return (
    <Box as="span" ref={ref} {...props}>
      <VisuallyHidden>{final}</VisuallyHidden>
      <span aria-hidden="true">
        {prefix}
        {value.toFixed(decimals)}
        {suffix}
      </span>
    </Box>
  );
};

export default Reveal;
